import type { ReactNode } from "react";
import { SidebarTrigger, useSidebar } from "@volca/ui/components/ui/sidebar";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { cn } from "@volca/ui/lib/utils";
import { useBusiness } from "@/core/business";
import { AppBreadcrumbs } from "./app-breadcrumbs";
import { useShellFrameMode } from "./shell-frame";
import { MAIN_CONTENT_ID } from "./skip-link";

interface PageAreaProps {
  children: ReactNode;
}

export const PageArea = ({ children }: PageAreaProps) => {
  const { selectedBusiness, isLoading } = useBusiness();
  const businessName = selectedBusiness?.name ?? "Volca";
  const { openMobile } = useSidebar();
  const canvas = useShellFrameMode() === "canvas";

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b px-3 md:hidden">
        <SidebarTrigger
          className="pointer-coarse:size-11"
          aria-label="Toggle sidebar"
          aria-expanded={openMobile}
        />
        {isLoading ? (
          <Skeleton className="h-4 w-28" />
        ) : (
          <span className="truncate text-sm font-medium" title={businessName}>
            {businessName}
          </span>
        )}
      </header>
      <AppBreadcrumbs />
      {/* The skip link's target. Focus lands here, not on the first link.
          A document scrolls here, so its scrollbar's room is always kept.
          A canvas fills this region and scrolls inside itself. */}
      <div
        id={MAIN_CONTENT_ID}
        tabIndex={-1}
        className={cn(
          "relative min-h-0 flex-1 outline-hidden [container:page/size]",
          {
            "flex flex-col overflow-hidden": canvas,
            "[scrollbar-gutter:stable] overflow-y-auto": !canvas,
          },
        )}
      >
        {children}
      </div>
    </div>
  );
};
